import { useAuth } from "@workos-inc/authkit-react";

/**
 * Hook that returns an authed fetch — wraps window.fetch, injecting the
 * current WorkOS access token as `Authorization: Bearer <jwt>`.
 *
 * Throws if the SDK is still loading or the user is not signed in. That
 * lets `useQuery` retry until auth is ready instead of issuing a silent
 * unauthenticated request that 401s and gets cached as a hard failure
 * (the bug that affected late-mounting pages like KbPage on first nav).
 *
 * Usage:
 *   const api = useApi();
 *   const res = await api("/api/clients", { method: "GET" });
 */
export function useApi() {
  const { getAccessToken, isLoading, user } = useAuth();

  return async function apiFetch(
    input: string,
    init: RequestInit = {},
  ): Promise<Response> {
    if (isLoading) {
      throw new Error("auth-not-ready: AuthKit SDK still loading");
    }
    if (!user) {
      throw new Error("auth-not-ready: not signed in");
    }

    let token: string | null = null;
    try {
      token = (await getAccessToken()) ?? null;
    } catch (err) {
      // getAccessToken can transiently reject right after SDK init while
      // the refresh-token round-trip completes. Surface to React Query
      // so it retries instead of caching a stale 401.
      throw new Error(
        `auth-not-ready: getAccessToken failed (${
          err instanceof Error ? err.message : String(err)
        })`,
      );
    }
    if (!token) {
      throw new Error("auth-not-ready: getAccessToken returned no token");
    }

    const headers = new Headers(init.headers ?? {});
    headers.set("Authorization", `Bearer ${token}`);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return fetch(input, { ...init, headers });
  };
}
